<script setup>
import Dialog from "primevue/dialog";
import dayjs from "dayjs";
import {computed, ref} from "vue";

const props = defineProps({
  formData: Object,
  hasDuplicatedReservations: Boolean,
  hasMyselfReservations: Boolean,
  windowWidth: Number,
  rooms: Array,
  workers: Array,
  designationTypes: Array,
  rentalServices: Array,
  postConfirmationData: Object
})
const emit = defineEmits(['submit', 'hideConfirmMessage', 'change'])
const isEditMode = ref(false)

const isAgreed = ref(false)

const getRoomById = (id) => {
  return props.rooms.find(r => r.id === id)
}

const getWorkerById = (id) => {
  return props.workers.find(w => w.id === id)
}

const getDesignationTypeById = (id) => {
  return props.designationTypes.find(t => t.id === id)
}

const getRentalServiceById = (id) => {
  return props.rentalServices.find(r => r.id === id)
}

const readyToSubmit = computed(() => {
  if (!props.formData.entry.signage_company) return false
  if (!props.formData.entry.signage_person) return false
  if (props.hasMyselfReservations) return false
  return isAgreed || props.hasDuplicatedReservations;
})


</script>

<template>
  <Dialog v-model:visible="formData.confirmVisible" modal header="以下の内容で予約します"
          :style="{width: windowWidth > 768 ? '70vw':'100vw'}">

    <div class="gap-8 overflow-y-auto text-sm max-h-[80vh] md:text-base">
      <div v-if="hasDuplicatedReservations" class="mb-3 ml-3">
        <div class="text-red-500">
          予約しようとしている日程の一部に、他のお客様からの予約で既に埋まっている日が含まれております。
        </div>
        <div class="text-gray-600">その日については「キャンセル待ち」にて受付させていただきます。</div>
        <div class="text-gray-600">会議室を予約する場合は、SMART BOOKINGから再度ご予約ください。</div>
      </div>
      <div v-if="hasMyselfReservations" class="mb-3 ml-3">
        <div class="text-red-500">予約しようとしている日程の一部に、すでに予約済の日が含まれております。</div>
        <div class="text-gray-600">重複日の新規受付はできません。</div>
      </div>
      <table class="w-full table-fixed">
        <tbody>
        <tr class="even:bg-gray-50">
          <td class="hidden md:inline-block">
            <div class="hidden md:inline-block md:px-4 md:py-3 md:text-gray-500">案内板表示</div>
          </td>
          <td class="w-2/3 p-3 md:m-0">
            <div class="text-gray-500 md:hidden">案内板表示</div>
            <div class="flex">
              <div>
                <div class="flex p-0 md:px-4 md:py-3">
                  <div>会社名:</div>
                  <div class="whitespace-pre-wrap break-all pl-2" v-show="!isEditMode">{{
                      formData.entry.signage_company
                    }}
                  </div>
                  <div>
                    <InputText type="text" class="w-52 py-0" :value="formData.entry.signage_company" v-show="isEditMode"
                               @change="emit('change', 'signage_company', $event)"></InputText>
                    <div class="text-sm text-red-400" v-show="!formData.entry.signage_company">入力してください</div>
                  </div>
                </div>
                <div class="flex p-0 pl-2 md:px-4 md:py-3">
                  <div>ご担当者様:</div>
                  <div class="whitespace-pre-wrap break-all pl-2" v-show="!isEditMode">{{
                      formData.entry.signage_person
                    }}&nbsp;{{ formData.entry.signage_person_suffix }}
                  </div>
                  <div>
                    <InputText type="text" class="w-32 py-0" :value="formData.entry.signage_person" v-show="isEditMode"
                               @change="emit('change', 'signage_person', $event)"></InputText>
                    <div class="text-sm text-red-400" v-show="!formData.entry.signage_person">入力してください</div>
                  </div>
                  <InputText type="text" class="w-14 py-0" :value="formData.entry.signage_person_suffix"
                             v-show="isEditMode" @change="emit('change', 'signage_person_suffix', $event)"></InputText>
                </div>
              </div>
              <div class="flex items-end p-0 pl-2 text-blue-400 md:px-4 md:py-3">
                <button @click="isEditMode=true" v-show="!isEditMode" class="flex">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5"
                       stroke="currentColor" class="mt-1 mr-1 h-4 w-4">
                    <path stroke-linecap="round" stroke-linejoin="round"
                          d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"/>
                  </svg>
                  編集
                </button>
                <button @click="isEditMode=false" v-show="isEditMode">OK</button>
              </div>
            </div>
          </td>
        </tr>
        <tr class="even:bg-gray-50">
          <td class="hidden md:inline-block">
            <div class="hidden md:inline-block md:px-4 md:py-3 md:text-gray-500">案件名(ご請求タイトル)</div>
          </td>
          <td class="p-3 md:m-0">
            <div class="text-gray-500 md:hidden">案件名(ご請求タイトル)</div>
            <div class="whitespace-pre-wrap break-all p-0 md:px-4 md:py-3">{{ formData.entry.name }}</div>
          </td>
        </tr>
        <tr class="even:bg-gray-50">
          <td class="hidden md:inline-block">
            <div class="hidden p-0 text-gray-500 md:inline-block md:px-4 md:py-3">予約日時</div>
          </td>
          <td class="p-3 md:m-0">
            <div class="text-gray-500 md:hidden">
              予約日時
            </div>
            <div class="whitespace-pre-wrap break-all p-0 md:px-4 md:py-3">
              <div v-for="(reservation, reservationIndex) in postConfirmationData.value.data.rental_room_reservations">
                <div class="mb-2 flex flex-col md:mb-0 md:flex-row">
                  <div class="flex items-center">
                    <span v-if="reservation.is_reserved"
                          class="ml-0 text-red-500 badge badge-sm badge-outline %> md:ml-3 md:hidden">キャンセル待ち申し込み</span>
                    <span v-if="reservation.is_reserved_myself"
                          class="ml-0 text-red-500 badge badge-sm badge-outline %> md:ml-3 md:hidden">既予約日</span>
                  </div>
                  <div>
                    <span>{{ dayjs(reservation.start_at).format('YYYY/M/D H:mm') }}</span>
                    <span class="mx-2">〜</span>
                    <span class="mr-1">{{ dayjs(reservation.end_at).format('YYYY/M/D H:mm') }}</span>
                    <span>({{ getRoomById(reservation.rental_room_id).name }})</span>
                    <span v-if="reservation.is_reserved"
                          class="ml-2 hidden text-red-500 badge badge-sm badge-outline %> md:inline-block">キャンセル待ち申し込み</span>
                    <span v-if="reservation.is_reserved_myself"
                          class="ml-2 hidden text-red-500 badge badge-sm badge-outline %> md:inline-block">既予約日</span>
                  </div>
                </div>
              </div>
            </div>
          </td>
        </tr>
        <tr class="even:bg-gray-50">
          <td class="hidden md:inline-block">
            <div class="hidden px-4 py-3 text-gray-500 md:inline-block">エディター</div>
          </td>
          <td class="p-3 md:m-0">
            <div class="text-gray-500 md:hidden md:px-4 md:py-3">エディター</div>
            <div class="whitespace-pre-wrap break-all p-0 md:px-4 md:py-3">
              <div>
                <div v-if="formData.entry.needsInsideWorker" class="mb-1">
                  <div class="mb-1">弊社エディターを希望</div>
                  <div class="ml-1">
                    <span class="mr-1">第一希望:{{ getWorkerById(formData.entry.firstDesignationUser)?.name }}</span>
                    <span>({{ getDesignationTypeById(formData.entry.firstDesignationType)?.name }})</span>
                  </div>
                  <div class="ml-1">
                    <span class="mr-1">第二希望:{{ getWorkerById(formData.entry.secondDesignationUser)?.name }}</span>
                    <span>({{ getDesignationTypeById(formData.entry.secondDesignationType)?.name }})</span>
                  </div>
                </div>
                <div v-if="formData.entry.hasOutsideWorker">
                  <div>外部スタッフが作業（お客様ご手配）</div>
                </div>
              </div>
            </div>
          </td>
        </tr>
        <tr class="even:bg-gray-50">
          <td class="hidden md:inline-block">
            <div class="hidden px-4 py-3 text-gray-500 md:inline-block">備考</div>
          </td>
          <td class="p-3 md:m-0">
            <div class="text-gray-500 md:hidden md:px-4 md:py-3">備考</div>
            <div class="whitespace-pre-wrap break-all p-0 md:px-4 md:py-3">{{ formData.entry.note }}</div>
          </td>
        </tr>
        <tr class="even:bg-gray-50">
          <td class="hidden md:inline-block">
            <div class="hidden px-4 py-3 text-gray-500 md:inline-block">オプションサービス</div>
          </td>
          <td class="p-3 md:m-0">
            <div class="text-gray-500 md:hidden md:px-4 md:py-3">オプションサービス</div>
            <div class="whitespace-pre-wrap break-all p-0 md:px-4 md:py-3">
              <div v-for="rentalServiceId in formData.entry.rentalServiceIds">
                <span>{{ getRentalServiceById(rentalServiceId)?.name }}</span>
              </div>
            </div>
          </td>
        </tr>
        <tr class="even:bg-gray-50">
          <td class="hidden md:inline-block">
            <div class="hidden px-4 py-3 text-gray-500 md:inline-block">通知先</div>
          </td>
          <td class="p-3 md:m-0">
            <div class="text-gray-500 md:hidden md:px-4 md:py-3">通知先</div>
            <div class="whitespace-pre-wrap break-all p-0 md:px-4 md:py-3">
              <div v-for="recipient in formData.entry.recipients">
                <span>{{ recipient.name }}</span>
              </div>
            </div>
          </td>
        </tr>
        </tbody>
      </table>
    </div>
    <template #footer>
      <div v-if="hasDuplicatedReservations" class="mb-4 flex items-center justify-end gap-2">
        <input type="checkbox" v-model="isAgreed" class="focus:ring-2 focus:ring-indigo-500:focus ring-offset-1 md:h-5 md:w-5 h-3 w-3 text-indigo-600 border-gray-300
 rounded"/>
        <label class="pb-0 text-xs text-gray-600 md:pb-1 md:text-base">
          キャンセル待ちの予約を含むことを確認しました
        </label>
      </div>
      <Button severity="secondary" outlined label="戻る" @click="emit('hideConfirmMessage')"/>
      <Button severity="success" label="登録" @click="emit('submit')" class="ml-4" :disabled="!readyToSubmit"/>
    </template>
  </Dialog>
</template>
